import { AfterViewInit, Directive, ElementRef, OnDestroy, effect, inject, input } from '@angular/core';
import * as textMask from 'vanilla-text-mask/dist/vanillaTextMask.js';

/**
 * Applies a digits-only date mask to a PrimeNG `<p-datepicker>` so the user types
 * only numbers and the `.` separators are inserted automatically (dd.MM.yyyy,
 * matching PrimeNG's `dateFormat="dd.mm.yy"`). Set `[u2bDateMaskTime]="true"` for
 * pickers with `[showTime]` to also mask a trailing ` HH:mm` segment; the mask
 * rebuilds when that flag toggles (e.g. an hourly subscription).
 *
 * The mask is attached to the datepicker's inner native input; PrimeNG still
 * parses the resulting text via its own input handler.
 */
@Directive({
    selector: '[u2bDateMask]',
    standalone: true,
})
export class U2bDateMaskDirective implements AfterViewInit, OnDestroy {

    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    // Bound as `[u2bDateMaskTime]`; set true on pickers with `[showTime]`.
    readonly u2bDateMaskTime = input<boolean>(false);

    private inputEl: HTMLInputElement | null = null;
    private controller: { destroy(): void } | null = null;

    constructor() {
        // Rebuild when the time segment toggles (date-only ⇄ date+time).
        effect(() => {
            const withTime = this.u2bDateMaskTime();
            if (this.inputEl) {
                this.apply(withTime);
            }
        });
    }

    ngAfterViewInit(): void {
        this.inputEl = this.host.nativeElement.querySelector('input');
        if (this.inputEl) {
            this.apply(this.u2bDateMaskTime());
        }
    }

    private apply(withTime: boolean): void {
        if (!this.inputEl) {
            return;
        }
        this.controller?.destroy();
        const dateMask = [/[0-3]/, /\d/, '.', /[0-1]/, /\d/, '.', /\d/, /\d/, /\d/, /\d/];
        const timeMask = [' ', /[0-2]/, /\d/, ':', /[0-5]/, /\d/];
        this.controller = textMask.maskInput({
            inputElement: this.inputEl,
            mask: withTime ? [...dateMask, ...timeMask] : dateMask,
            guide: false,
            keepCharPositions: false,
        });
    }

    ngOnDestroy(): void {
        this.controller?.destroy();
    }
}
